import { type TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { Card } from "@src/appV2/redesign/system/ui-components";
import { isAfter, parseISO } from "date-fns";
import { type ReactNode } from "react";

import { BookingCardContentWrapper } from "../CardContentWrapper";
import { ShiftCardSummaryInfoDeprecated } from "../Open/CardSummaryInfoDeprecated";
import { ShiftCardPillsWrapper } from "../PillsWrapper";
import { BookingCardSummaryWrapper } from "../SummaryWrapper";
import { TimeSlotIndicator } from "../TimeSlotIndicator";
import { WorkerShiftCardFooter } from "./Footer";

export interface ShiftWithContentCardProps {
  pills?: ReactNode[];
  children: ReactNode;
  shiftTimeSlot: TimeSlot;
  shiftStart: string;
  shiftEnd: string;
  shiftQualificationName: string;
  shiftHourlyPay?: string;
  shiftTotalPay?: string;
  shiftWorkDurationInHours?: number;

  /**
   * If provided, there will be a check to see if the shift has passed start date
   * It will display "Now" instead of the start time
   */
  currentDate?: Date;

  workplaceName: string;
  workplaceTimezone: string;
}

export function ShiftWithContentCard(props: ShiftWithContentCardProps) {
  const {
    pills,
    children,
    shiftTimeSlot,
    shiftStart,
    shiftEnd,
    shiftQualificationName,
    shiftHourlyPay,
    shiftTotalPay,
    shiftWorkDurationInHours,
    currentDate,
    workplaceName,
    workplaceTimezone,
  } = props;

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    shiftQualificationName,
  ]);
  const qualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(shiftQualificationName) ?? shiftQualificationName;

  const shiftStartDate = parseISO(shiftStart);
  const useNowForStartDate = isDefined(currentDate) && isAfter(currentDate, shiftStartDate);

  return (
    <Card sx={{ flexGrow: 1 }}>
      <BookingCardContentWrapper>
        <BookingCardSummaryWrapper>
          <TimeSlotIndicator timeSlot={shiftTimeSlot} />

          <ShiftCardSummaryInfoDeprecated
            dateRange={{ startDate: shiftStartDate, endDate: parseISO(shiftEnd) }}
            timezone={workplaceTimezone}
            deviceTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
            durationInHours={shiftWorkDurationInHours}
            workplaceName={workplaceName}
            useNowForStartDate={useNowForStartDate}
          >
            {isDefined(pills) && <ShiftCardPillsWrapper>{pills}</ShiftCardPillsWrapper>}
          </ShiftCardSummaryInfoDeprecated>
        </BookingCardSummaryWrapper>

        <WorkerShiftCardFooter
          hourlyPay={shiftHourlyPay}
          totalPay={shiftTotalPay}
          qualificationText={qualificationDisplayLabel}
          isLoadingQualification={false}
        />

        {children}
      </BookingCardContentWrapper>
    </Card>
  );
}
